import React, { useState, useRef, useEffect } from "react";
import { Modal, Input, DatePicker, Select, Tabs } from "antd";
import {
  LeftOutlined, RightOutlined, PlusOutlined,
  LoadingOutlined, AudioOutlined, HomeOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  MedicineBoxOutlined
} from "@ant-design/icons";
import "./CalendarModule.scss";
import { useNavigate } from "react-router-dom";
import { useListingEventsQuery, useAddEventsMutation } from "../../utils/apiSlice";
import { useEffectiveUserData } from "../../hooks/useEffectiveUserData";
import dayjs from "dayjs";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { createPortal } from "react-dom";
const { Option } = Select;
const { TextArea } = Input;

// ── MedPopup ─────────────────────────────────────────────────────
const MedPopup = ({ med, visible, position }: {
  med: any;
  visible: boolean;
  position: { top: number; left: number };
}) => {
  if (!visible || !med) return null;

  const intake = med.intakeTime || {};
  const slots = [
    intake.morning && { label: "Morning", ...intake.morning },
    intake.noon && { label: "Noon", ...intake.noon },
    intake.evening && { label: "Evening", ...intake.evening },
    intake.night && { label: "Night", ...intake.night },
  ].filter(Boolean);

  return createPortal(
    <div
      className="medication-portal-tooltip"
      style={{ top: position.top, left: position.left }}
    >
      {/* Header */}
      <div className="medication-portal-tooltip__header">
        <div className="medication-portal-tooltip__icon-wrapper">
          <MedicineBoxOutlined />
        </div>
        <div className="medication-portal-tooltip__title-group">
          <h4 className="title">{med.medicationName}</h4>
          <span className="type">{med.medicationType}</span>
        </div>
      </div>

      {/* Body */}
      <div className="medication-portal-tooltip__body">
        <div className="medication-portal-tooltip__frequency">
          <span className="label">Frequency</span>
          <span className="count">{med.frequencyPerDay}x per day</span>
        </div>

        {/* Time Slots */}
        {slots.map((slot) => (
          <div key={slot.label} className="medication-portal-tooltip__slot-card">
            <div className="slot-left">
              <span className="label">{slot.label}</span>
              <span className="time">{slot.time}</span>
            </div>
            <div className="slot-right">
              <span className="dose">Dose: {slot.dosage}</span>
              <span className="instruction">{slot.mealInstruction}</span>
            </div>
          </div>
        ))}

        {/* Notes */}
        {med.additionalNotes && (
          <div className="medication-portal-tooltip__notes">
            <FileTextOutlined style={{ color: '#bfbfbf', marginTop: '3px' }} />
            <span>{med.additionalNotes}</span>
          </div>
        )}

        {/* Status */}
        <div className="medication-portal-tooltip__status-badge">
          <span className="dot" />
          Active
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── EventPopup ───────────────────────────────────────────────────
const EventPopup = ({ event, visible, position }: {
  event: any;
  visible: boolean;
  position: { top: number; left: number };
}) => {
  if (!visible || !event) return null;

  const isLab = event.appointmentType === "LAB";

  return createPortal(
    <div
      className="medical-portal-tooltip"
      style={{ top: position.top, left: position.left }}
    >
      {/* Header Section */}
      <div className="medical-portal-tooltip__header">
        <div className={`medical-portal-tooltip__icon-wrapper ${isLab ? '--lab' : '--doctor'}`}>
          {isLab ? <ExperimentOutlined /> : <MedicineBoxOutlined />}
        </div>
        <div className="medical-portal-tooltip__title-group">
          <h4 className="title">{isLab ? event.labTestName : event.doctorName}</h4>
          <span className="subtitle">{isLab ? "Laboratory Analysis" : "Medical Consultation"}</span>
        </div>
      </div>

      {/* Info Section */}
      <div className="medical-portal-tooltip__content">
        <div className="medical-portal-tooltip__data-row">
          <span className="label">Scheduled Time</span>
          <span className="value">{event.time?.slice(0, 5)}</span>
        </div>

        <div className="medical-portal-tooltip__data-row">
          <span className="label">Appointment Date</span>
          <span className="value">{event.date}</span>
        </div>

        {/* Notes Section */}
        {event.notes?.trim() && (
          <div className="medical-portal-tooltip__notes">
            <FileTextOutlined style={{ marginTop: '2px', color: '#bfbfbf' }} />
            <span>{event.notes}</span>
          </div>
        )}

        {/* Status Badge */}
        <div className={`medical-portal-tooltip__badge ${isLab ? '--lab' : '--doctor'}`}>
          <span className="dot" />
          {isLab ? "Lab Test" : "Doctor"}
        </div>
      </div>
    </div>,
    document.body
  );
};
// ── MedCard — tracks mouse position for accurate popup placement ─
const MedCard = ({ med, className, children }: {
  med: any;
  className: string;
  children: React.ReactNode;
}) => {
  const [popup, setPopup] = useState<{ visible: boolean; top: number; left: number }>({
    visible: false, top: 0, left: 0,
  });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const popupWidth = 240;
    const spaceRight = window.innerWidth - rect.right;

    // ✅ flip left if not enough space on right
    const left = spaceRight >= popupWidth + 10
      ? rect.right + 8
      : rect.left - popupWidth - 8;

    // ✅ clamp top so popup doesn't go below viewport
    const popupHeight = 200;
    const top = rect.top + popupHeight > window.innerHeight
      ? window.innerHeight - popupHeight - 10
      : rect.top;

    setPopup({ visible: true, top, left });
  };

  const handleMouseLeave = () => setPopup({ visible: false, top: 0, left: 0 });

  return (
    <>
      <div
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "pointer", position: "relative" }}
      >
        {children}
      </div>
      <MedPopup med={med} visible={popup.visible} position={{ top: popup.top, left: popup.left }} />
    </>
  );
};



// ── EventCard with hover ─────────────────────────────────────────
const EventCard = ({ event, className, children }: {
  event: any;
  className: string;
  children: React.ReactNode;
}) => {
  const [popup, setPopup] = useState<{ visible: boolean; top: number; left: number }>({
    visible: false, top: 0, left: 0,
  });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const popupWidth = 240;
    const spaceRight = window.innerWidth - rect.right;
    const left = spaceRight >= popupWidth + 10
      ? rect.right + 8
      : rect.left - popupWidth - 8;

    const popupHeight = 200;
    const top = rect.top + popupHeight > window.innerHeight
      ? window.innerHeight - popupHeight - 10
      : rect.top;

    setPopup({ visible: true, top, left });
  };

  const handleMouseLeave = () => setPopup({ visible: false, top: 0, left: 0 });

  return (
    <>
      <div
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "pointer" }}
      >
        {children}
      </div>
      <EventPopup event={event} visible={popup.visible} position={{ top: popup.top, left: popup.left }} />
    </>
  );
};

// ── Main Component ───────────────────────────────────────────────
export default function CalendarModule() {

  const [labForm, setLabForm] = useState({ familyMember: "", labTestName: "", date: "", time: "", notes: "" })
  const [docterForm, setDocterForm] = useState({ familyMember: "", doctorName: "", date: "", time: "", notes: "" })

  const [view, setView] = useState("month");
  const [selectedFilter, setSelectedFilter] = useState("Medications");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [audioURL, setAudioURL] = useState<string>("");
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const nameValidation = Yup.string()
    .trim()
    .min(2, "Must be at least 2 characters")
    .max(50, "Cannot exceed 50 characters")
    .matches(/^[A-Za-z\s]+$/, "Only alphabets and spaces are allowed")
    .required("This field is required *");
  const doctorSchema = Yup.object({
    familyMember: Yup.string().required("Please select a family member *"),
    doctorName: nameValidation.label("Doctor name *"),
    date: Yup.string().required("Date is required *"),
    time: Yup.string().required("Time is required *"),
    notes: Yup.string().max(2000, "Notes cannot exceed 2000 characters"),

  });
  const MAX_LENGTH = 2000;
  const labSchema = Yup.object({
    familyMember: Yup.string().required("Please select a family member *"),
    labTestName: Yup.string().required("Lab test name *"),
    date: Yup.string().required("Date is required *"),
    time: Yup.string().required("Time is required *"),
    notes: Yup.string().max(2000, "Notes cannot exceed 2000 characters"),
  });
  const [activeTab, setActiveTab] = useState<any>('1');
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (isModalOpen) {
      setFormKey(prev => prev + 1);
    }
  }, [isModalOpen]);
  const generateTimeSlots = () => {
    const slots: string[] = [];

    for (let i = 0; i < 24; i++) {
      const hour = i % 12 === 0 ? 12 : i % 12;
      const ampm = i < 12 ? "AM" : "PM";

      slots.push(`${hour.toString().padStart(2, "0")}:00 ${ampm}`);
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();

  const weekDays = [
    { name: "Sun", num: currentDate.startOf("week").date() },
    { name: "Mon", num: currentDate.startOf("week").add(1, "day").date() },
    { name: "Tue", num: currentDate.startOf("week").add(2, "day").date() },
    { name: "Wed", num: currentDate.startOf("week").add(3, "day").date() },
    { name: "Thu", num: currentDate.startOf("week").add(4, "day").date() },
    { name: "Fri", num: currentDate.startOf("week").add(5, "day").date() },
    { name: "Sat", num: currentDate.startOf("week").add(6, "day").date() },
  ];

  let startDate = "";
  let endDate = "";
  if (view === "day") {
    startDate = currentDate.format("YYYY-MM-DD");
    endDate = currentDate.format("YYYY-MM-DD");
  } else if (view === "week") {
    startDate = currentDate.startOf("week").format("YYYY-MM-DD");
    endDate = currentDate.endOf("week").format("YYYY-MM-DD");
  } else if (view === "month") {
    startDate = currentDate.startOf("month").format("YYYY-MM-DD");
    endDate = currentDate.endOf("month").format("YYYY-MM-DD");
  }

  const userData = useEffectiveUserData();
  const patientId = userData?.patientId || 36;



  const getAppointmentType = (filter: string) => {
    if (filter === "Doctor_Appointment") return "DOCTOR";
    if (filter === "Lab_test") return "LAB";
    return undefined; // Medications — don't send appointmentType
  };

  const { data: eventData, isLoading } = useListingEventsQuery(
    { patientId, appointmentType: getAppointmentType(selectedFilter), startDate, endDate },
    { skip: !patientId || selectedFilter === "Medications" } // ← skip for Medications
  );
  const eventsByDate: Record<string, any[]> = {};
  eventData?.calendarEvents?.forEach((event: any) => {
    if (!eventsByDate[event.date]) eventsByDate[event.date] = [];
    eventsByDate[event.date].push(event);
  });

  // ── filter events by type ──
  const getFilteredEvents = (date: string) => {
    const events = eventsByDate[date] || [];
    if (selectedFilter === "Lab_test") return events.filter((e: any) => e.appointmentType === "LAB");
    if (selectedFilter === "Doctor_Appointment") return events.filter((e: any) => e.appointmentType === "DOCTOR");
    return [];
  };
  // console.log(getFilteredEvents())



  const [addEvents, { isLoading: isSaving }] = useAddEventsMutation();


  const medicationByDate: Record<string, any[]> = {};
  eventData?.drugIntakeResponse?.medicationSchedule?.forEach((schedule: any) => {
    if (schedule.medications?.length > 0) {
      medicationByDate[schedule.startDate] = schedule.medications;
    }
  });

  const handleToday = () => setCurrentDate(dayjs());

  const handlePrev = () => {
    if (view === "day") setCurrentDate((d: any) => d.subtract(1, "day"));
    if (view === "week") setCurrentDate((d: any) => d.subtract(1, "week"));
    if (view === "month") setCurrentDate((d: any) => d.subtract(1, "month"));
  };

  const handleNext = () => {
    if (view === "day") setCurrentDate((d: any) => d.add(1, "day"));
    if (view === "week") setCurrentDate((d: any) => d.add(1, "week"));
    if (view === "month") setCurrentDate((d: any) => d.add(1, "month"));
  };

  const getIntakeInfo = (med: any) => {
    const intake = med.intakeTime || {};
    const slot = intake.morning || intake.noon || intake.evening || intake.night;
    return { time: slot?.time || "", meal: slot?.mealInstruction || "", dosage: slot?.dosage || "" };
  };

  const [transcript, setTranscript] = useState('')
  const handleNameInput = (value: string) => {
    return value
      .replace(/[^A-Za-z\s]/g, "")
      .replace(/\s+/g, " ")
      .trimStart();
  };
  const recognitionRef = useRef<any>(null);
  const disablePastDates = (current: any) => {
    return current && current < new Date().setHours(0, 0, 0, 0);
  };
  const headerLabel =
    view === "day" ? currentDate.format("MMMM D, YYYY") :
      view === "week" ? `${currentDate.startOf("week").format("MMM D")} - ${currentDate.endOf("week").format("MMM D, YYYY")}` :
        currentDate.format("MMMM YYYY");

  const startRecording = async () => {
    try {
      // 🔍 Check audio input devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasMic = devices.some(device => device.kind === "audioinput");

      if (!hasMic) {
        toast.error("Please connect a microphone or headset");
        return;
      }

      // 🎤 Ask permission + get stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (e) =>
        audioChunks.current.push(e.data);

      mediaRecorder.current.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: "audio/wav" });
        setAudioURL(URL.createObjectURL(blob));
      };

      // 🎤 Speech Recognition
      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;

      if (!SpeechRecognition) {
        toast.error("Speech recognition not supported in this browser");
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = "en-IN";
      recognition.interimResults = false;

      recognition.onresult = (event: any) => {
        const text = event.results[0][0].transcript;

        console.log("Text:", text);
        setTranscript(text);

        if (activeTab == 1) {
          setDocterForm((prev: any) => ({
            ...prev,
            notes: text,
          }));
        } else {
          setLabForm((prev: any) => ({
            ...prev,
            notes: text,
          }));
        }
      };

      recognition.onerror = (e: any) => {
        console.error("Speech error:", e);
        toast.error("Speech recognition error");
      };

      recognition.start();
      recognitionRef.current = recognition;

      // 🎙 Start recording
      mediaRecorder.current.start();
      setIsRecording(true);

    } catch (err: any) {
      console.error("Mic error:", err);

      // ✅ Better user messages
      if (err.name === "NotAllowedError") {
        toast.error("Please allow microphone access");
      } else if (err.name === "NotFoundError") {
        toast.error("No microphone found. Please connect headset");
      } else {
        toast.error("Unable to start recording");
      }
    }
  };
  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      recognitionRef.current?.stop();

      setIsRecording(false);
      mediaRecorder.current.stream.getTracks().forEach((t) => t.stop());
    }
  };



  const VoiceNote = () => (
    <div className="voice-note-container mt-2">
      {!isRecording ? (
        <div className="voice-btn" onClick={startRecording}>
          <AudioOutlined className="mike-icon" /><span>Click to add voice note</span>
        </div>
      ) : (
        <div className="voice-btn recording" onClick={stopRecording}>
          <LoadingOutlined className="mike-icon" /><span>Recording... (Click to Stop)</span>
        </div>
      )}
      {/* {audioURL && !isRecording && (
        <div className="audio-player mt-2">
          <audio src={audioURL} controls />
          <button className="btn btn-sm text-danger ms-2" onClick={() => setAudioURL("")}>Delete</button>
        </div>
      )} */}
    </div>
  );

  return (
    <div className="content-wrapper calendar-module-wrapper">
      <div className="container-fluid py-4">
        <div className="page-header">
          <div className="breadcrumb">
            <h3 className="page-title">Calendar</h3>
            <span className="customize-breadcrumb">
              <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
              <span className="separator"> - </span>
              <span className="current">Calendar</span>
            </span>
          </div>
        </div>

        <div className="calendar-container">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <h4 className="fw-bold m-0">Calendar</h4>
            <div className="d-flex align-items-center gap-3 ms-auto">
              <div className="btn-group shadow-sm">
                <button className="btn btn-sm" onClick={handleToday}>Today</button>
                <button className="btn btn-sm" onClick={handlePrev}><LeftOutlined /></button>
                <button className="btn btn-sm" onClick={handleNext}><RightOutlined /></button>
              </div>
              <span className="fw-semibold">{headerLabel}</span>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="btn-group shadow-sm">
              <button className={`btn btn-sm ${view === 'day' ? 'active' : ''}`} onClick={() => setView('day')}>Day</button>
              <button className={`btn btn-sm ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}>Week</button>
              <button className={`btn btn-sm ${view === 'month' ? 'active' : ''}`} onClick={() => setView('month')}>Month</button>
            </div>
            <div className="d-flex gap-2">
              <Select size="small" style={{ width: 160 }} value={selectedFilter} onChange={(val) => setSelectedFilter(val)}>
                <Option value="Medications">Medications</Option>
                <Option value="Lab_test">Lab Test</Option>
                <Option value="Doctor_Appointment">Doctor Appointment</Option>
              </Select>
              <button className="btn btn-new btn-sm" onClick={() => setIsModalOpen(true)}>
                <PlusOutlined className="me-1" /> New
              </button>
            </div>
          </div>
          {/* ── DAY VIEW ── */}
          {view === "day" && (
            <div className="calendar-grid border rounded">
              {isLoading ? <div className="p-4 text-center"><LoadingOutlined /> Loading...</div> : (
                timeSlots.map((time, idx) => {
                  const todayStr = currentDate.format("YYYY-MM-DD");

                  // ── medications ──
                  const meds = medicationByDate[todayStr] || [];
                  const slotMeds = selectedFilter === "Medications"
                    ? meds.filter((med: any) => {
                      const intake = med.intakeTime || {};
                      const t = intake.morning?.time || intake.noon?.time || intake.evening?.time || intake.night?.time || "";
                      return t === time;
                    })
                    : [];

                  // ── events (LAB / DOCTOR) ──
                  const events = getFilteredEvents(todayStr).filter((e: any) => {
                    const eTime = e.time?.slice(0, 5); // "12:39"
                    const slotTime = dayjs(`2000-01-01 ${time}`, "YYYY-MM-DD hh:mm A").format("HH:mm");
                    return eTime === slotTime;
                  });

                  return (
                    <div className="time-row" key={idx}>
                      <div className="time-label">{time.split(' ')[0]}<br />{time.split(' ')[1]}</div>
                      <div className="slot-content">
                        {slotMeds.map((med: any) => {
                          const { time: t, meal, dosage } = getIntakeInfo(med);
                          return (
                            <MedCard key={med.patientMedicationId} med={med} className="apt-card-horizontal apt-confirmed">
                              <div className="user-info">
                                <div>
                                  <p className="patient-name">{med.medicationName}</p>
                                  <p className="apt-details">{t} | {med.medicationType} | Dosage: {dosage}</p>
                                </div>
                              </div>
                              <div className="apt-meta">
                                <span className="status-badge badge-confirmed">Active</span>
                                <span className="type-label">{meal}</span>
                              </div>
                            </MedCard>
                          );
                        })}
                        {events.map((event: any) => (
                          <EventCard key={event.id} event={event}
                            className={`apt-card-horizontal ${event.appointmentType === "LAB" ? "apt-lab" : "apt-confirmed"}`}>
                            <div className="user-info">
                              <div>
                                <p className="patient-name">{event.appointmentType === "LAB" ? event.labTestName : event.doctorName}</p>
                                <p className="apt-details">{event.time?.slice(0, 5)} | {event.appointmentType === "LAB" ? "Lab Test" : "Doctor Appointment"}</p>
                              </div>
                            </div>
                            <div className="apt-meta">
                              <span className={`status-badge ${event.appointmentType === "LAB" ? "badge-lab" : "badge-confirmed"}`}>
                                {event.appointmentType === "LAB" ? "Lab" : "Doctor"}
                              </span>
                              {event.notes && <span className="type-label">{event.notes}</span>}
                            </div>
                          </EventCard>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── WEEK VIEW ── */}
          {view === "week" && (
            <div className="week-grid">
              {isLoading ? <div className="p-4 text-center"><LoadingOutlined /> Loading...</div> : (
                weekDays.map((day, idx) => {
                  const colDate = currentDate.startOf("week").add(idx, "day").format("YYYY-MM-DD");
                  const meds = selectedFilter === "Medications" ? (medicationByDate[colDate] || []) : [];
                  const events = getFilteredEvents(colDate);
                  const isToday = colDate === dayjs().format("YYYY-MM-DD");

                  return (
                    <div className={`day-column ${isToday ? "today-highlight" : ""}`} key={idx}>
                      <div className="day-header">
                        <p className={`day-name ${isToday ? "text-primary fw-bold" : ""}`}>{day.name}</p>
                        <p className={`day-number ${isToday ? "active-today-num" : ""}`}>{day.num}</p>
                      </div>
                      <div className="day-body">
                        {meds.length === 0 && events.length === 0 && (
                          <div className="no-apt">
                            No{" "}
                            {
                              selectedFilter === "Medications"
                                ? "medications"
                                : selectedFilter === "Lab_test"
                                  ? "lab tests"
                                  : selectedFilter === "Doctor_Appointment"
                                    ? "appointments"
                                    : "records"
                            }
                          </div>
                        )}
                        {meds.map((med: any) => {
                          const { time, meal } = getIntakeInfo(med);
                          return (
                            <MedCard key={med.patientMedicationId} med={med} className="apt-card bg-blue">
                              <p className="apt-title">{med.medicationName} <span>{time}</span></p>
                              <p className="apt-sub">{med.medicationType}</p>
                              <p className="apt-sub">{meal}</p>
                            </MedCard>
                          );
                        })}
                        {events.map((event: any) => (
                          <EventCard key={event.id} event={event}
                            className={`apt-card ${event.appointmentType === "LAB" ? "bg-orange" : "bg-blue"}`}>
                            <p className="apt-title">
                              {event.appointmentType === "LAB" ? event.labTestName : event.doctorName}
                              <span>{event.time?.slice(0, 5)}</span>
                            </p>
                            <p className="apt-sub">{event.appointmentType === "LAB" ? "Lab Test" : "Doctor Appointment"}</p>
                            {event.notes && <p className="apt-sub">{event.notes}</p>}
                          </EventCard>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── MONTH VIEW ── */}
          {view === "month" && (
            <div className="month-view-container">
              <div className="row g-0 text-center">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                  <div key={d} className="col weekday-header">{d}</div>
                ))}
              </div>
              {isLoading ? <div className="p-4 text-center"><LoadingOutlined /> Loading...</div> : (
                <div className="month-grid">
                  {[...Array(35)].map((_, i) => {
                    const firstDayOfMonth = currentDate.startOf("month").day();
                    const dayNum = i - firstDayOfMonth + 1;
                    const daysInMonth = currentDate.daysInMonth();
                    const isValidDay = dayNum >= 1 && dayNum <= daysInMonth;
                    const cellDate = isValidDay ? currentDate.date(dayNum).format("YYYY-MM-DD") : null;
                    const meds = (cellDate && selectedFilter === "Medications") ? medicationByDate[cellDate] || [] : [];
                    const events = cellDate ? getFilteredEvents(cellDate) : [];
                    const isToday = cellDate === dayjs().format("YYYY-MM-DD");

                    return (
                      <div key={i} className={`day-cell ${isValidDay ? 'has-day' : ''} ${isToday ? "today-highlight" : ""}`}>
                        {isValidDay && (
                          <>
                            <span className="day-number">{dayNum}</span>
                            {meds.map((med: any) => {
                              const { time } = getIntakeInfo(med);
                              return (
                                <MedCard key={med.patientMedicationId} med={med} className="month-apt apt-blue">
                                  {time} - {med.medicationName}
                                </MedCard>
                              );
                            })}
                            {events.map((event: any) => (
                              <EventCard key={event.id} event={event}
                                className={`month-apt ${event.appointmentType === "LAB" ? "apt-orange" : "apt-blue"}`}>
                                {event.time?.slice(0, 5)} - {event.appointmentType === "LAB" ? event.labTestName : event.doctorName}
                              </EventCard>
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>


      <Modal
        title={<span>Add Calendar Event</span>}
        open={isModalOpen}
        onCancel={() => { setIsModalOpen(false); setAudioURL(""); }}
        footer={null} // ← Formik controls submit
        width={800}
        centered
        className="appointment-modal"
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "1",
              label: "Doctor Appointment",
              children: (
                <Formik
                  key={formKey}
                  initialValues={docterForm}
                  validationSchema={doctorSchema}
                  enableReinitialize
                  onSubmit={async (values, { setSubmitting, resetForm }) => {
                    try {
                      await addEvents({
                        patientId,
                        appointmentType: "DOCTOR",
                        doctorName: values.doctorName,
                        date: values.date,
                        time: values.time,
                        notes: values.notes,

                      }).unwrap();
                      resetForm();
                      setAudioURL("");
                      setIsModalOpen(false);
                      toast.success("Add Successfully")
                    } catch (err) {
                      console.error("Save failed:", err);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ setFieldValue, isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="row g-3">

                        {/* Family Member */}
                        <div className="col-12">
                          <label className="form-label">Family Member <span style={{ color: 'red' }}>*</span></label>
                          <Field name="familyMember">
                            {({ field }: any) => (
                              <Select
                                placeholder="Select member"
                                style={{ width: "100%" }}
                                value={field.value || undefined}
                                onChange={(val) => setFieldValue("familyMember", val)}
                                status={touched.familyMember && errors.familyMember ? "error" : ""}
                              >
                                <Option value="SPOUSE">Spouse</Option>
                                <Option value="PARENT">Parent</Option>
                                <Option value="CHILD">Child</Option>
                                <Option value="SIBLING">Sibling</Option>
                                <Option value="FRIEND">Friend</Option>
                                <Option value="OTHER">Other</Option>
                              </Select>
                            )}
                          </Field>
                          <ErrorMessage name="familyMember">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Doctor Name */}
                        <div className="col-md-6">
                          <label className="form-label">Doctor <span style={{ color: 'red' }}>*</span></label>
                          <Field name="doctorName">
                            {({ field }: any) => (
                              <Input
                                {...field}
                                placeholder="Dr. Sharma"
                                value={field.value}
                                onChange={(e) => {
                                  const clean = handleNameInput(e.target.value);
                                  field.onChange({
                                    target: { name: field.name, value: clean },
                                  });
                                }}
                                status={touched.doctorName && errors.doctorName ? "error" : ""}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="doctorName">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Date */}
                        <div className="col-md-3">
                          <label className="form-label">Date <span style={{ color: 'red' }}>*</span></label>
                          <DatePicker
                            style={{ width: "100%" }}
                            status={touched.date && errors.date ? "error" : ""}
                            onChange={(_, dateStr) => setFieldValue("date", dateStr)}
                            disabledDate={disablePastDates}
                          />
                          <ErrorMessage name="date">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Time */}
                        <div className="col-md-3">
                          <label className="form-label">Time <span style={{ color: 'red' }}>*</span></label>
                          <Field name="time">
                            {({ field }: any) => (
                              <Input
                                {...field}
                                type="time"
                                status={touched.time && errors.time ? "error" : ""}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="time">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Notes */}
                        <div className="col-12">
                          <label className="form-label">Notes</label>
                          <Field name="notes">
                            {({ field, form }: any) => {
                              const remaining = MAX_LENGTH - (form.values.notes?.length || 0);

                              return (
                                <>
                                  <TextArea
                                    {...field}
                                    rows={3}
                                    maxLength={MAX_LENGTH} // restrict input
                                    placeholder="Add your notes here..."
                                  />

                                  {/* Remaining characters */}
                                  <div
                                    style={{
                                      textAlign: "right",
                                      fontSize: "12px",
                                      color: remaining < 100 ? "red" : "#888"
                                    }}
                                  >
                                    {remaining}/2000
                                  </div>
                                </>
                              );
                            }}
                          </Field>

                          <ErrorMessage name="notes">
                            {(msg) => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                          {activeTab == 1 && <VoiceNote />}
                        </div>

                        {/* Footer buttons */}
                        <div className="col-12 d-flex justify-content-end gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setIsModalOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary px-40"
                            disabled={isSubmitting || isSaving}
                          >
                            {isSubmitting || isSaving ? <><LoadingOutlined className="me-1" /> Saving...</> : "Save"}
                          </button>
                        </div>

                      </div>
                    </Form>
                  )}
                </Formik>
              ),
            },
            {
              key: "2",
              label: "Lab Test",
              children: (
                <Formik
                  key={formKey}
                  initialValues={labForm}
                  validationSchema={labSchema}
                  enableReinitialize
                  onSubmit={async (values, { setSubmitting, resetForm }) => {
                    try {
                      await addEvents({
                        patientId,
                        appointmentType: "LAB",
                        labTestName: values.labTestName,

                        date: values.date,
                        time: values.time,
                        notes: values.notes,

                      }).unwrap();
                      resetForm();
                      setAudioURL("");
                      setIsModalOpen(false);
                      toast.success("Add Successfully")
                    } catch (err) {
                      console.error("Save failed:", err);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ setFieldValue, isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="row g-3">

                        {/* Family Member */}
                        <div className="col-12">
                          <label className="form-label">Family Member <span style={{ color: 'red' }}>*</span></label>
                          <Field name="familyMember">
                            {({ field }: any) => (
                              <Select
                                placeholder="Select member"
                                style={{ width: "100%" }}
                                value={field.value || undefined}
                                onChange={(val) => setFieldValue("familyMember", val)}
                                status={touched.familyMember && errors.familyMember ? "error" : ""}
                              >
                                <Option value="SPOUSE">Spouse</Option>
                                <Option value="PARENT">Parent</Option>
                                <Option value="CHILD">Child</Option>
                                <Option value="SIBLING">Sibling</Option>
                                <Option value="FRIEND">Friend</Option>
                                <Option value="OTHER">Other</Option>


                              </Select>
                            )}
                          </Field>
                          <ErrorMessage name="familyMember">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Lab Test Name */}
                        <div className="col-md-6">
                          <label className="form-label">Lab Test Name <span style={{ color: 'red' }}>*</span></label>
                          <Field name="labTestName">
                            {({ field }: any) => (
                              <Input
                                {...field}
                                placeholder="HbA1c / X-Ray"
                                value={field.value}

                                status={touched.labTestName && errors.labTestName ? "error" : ""}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="labTestName">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Date */}
                        <div className="col-md-3">
                          <label className="form-label">Date <span style={{ color: 'red' }}>*</span></label>
                          <DatePicker
                            style={{ width: "100%" }}
                            status={touched.date && errors.date ? "error" : ""}
                            onChange={(_, dateStr) => setFieldValue("date", dateStr)}
                            disabledDate={disablePastDates}
                          />
                          <ErrorMessage name="date">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Time */}
                        <div className="col-md-3">
                          <label className="form-label">Time <span style={{ color: 'red' }}>*</span></label>
                          <Field name="time">
                            {({ field }: any) => (
                              <Input
                                {...field}
                                type="time"
                                status={touched.time && errors.time ? "error" : ""}
                              />
                            )}
                          </Field>
                          <ErrorMessage name="time">
                            {msg => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                        </div>

                        {/* Notes */}
                        <div className="col-12">
                          <label className="form-label">Notes</label>
                          <Field name="notes">
                            {({ field, form }: any) => {
                              const remaining = MAX_LENGTH - (form.values.notes?.length || 0);

                              return (
                                <>
                                  <TextArea
                                    {...field}
                                    rows={3}
                                    maxLength={MAX_LENGTH} // restrict input
                                    placeholder="Add your notes here..."
                                  />

                                  {/* Remaining characters */}
                                  <div
                                    style={{
                                      textAlign: "right",
                                      fontSize: "12px",
                                      color: remaining < 100 ? "red" : "#888"
                                    }}
                                  >
                                    {remaining}/2000
                                  </div>
                                </>
                              );
                            }}
                          </Field>

                          <ErrorMessage name="notes">
                            {(msg) => <div className="text-danger small mt-1">{msg}</div>}
                          </ErrorMessage>
                          {activeTab == 2 && <VoiceNote />}
                        </div>

                        {/* Footer buttons */}
                        <div className="col-12 d-flex justify-content-end gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setIsModalOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary px-40"
                            disabled={isSubmitting || isSaving}
                          >
                            {isSubmitting || isSaving ? <><LoadingOutlined className="me-1" /> Saving...</> : "Save"}
                          </button>
                        </div>

                      </div>
                    </Form>
                  )}
                </Formik>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}